import React, { useState, useEffect, useCallback } from 'react';
import {
  Trophy, RefreshCw,
  ChevronRight, ChevronDown, Bot,
  BarChart3, Sun,
  Copy, TrendingUp, TrendingDown, ExternalLink
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import { AgentLeaderboardEntry, AgentTrade, DailySummary } from '../../types';
import AgentDetailModal from '../agents/AgentDetailModal';
import TodaySummary from './TodaySummary';
import { pnlColor, pnlColor50, pnlColor30 } from '../../utils/pnl';
import { formatDateTime } from '../../utils/format';
import { dteBadgeDays } from '../../utils/etDate';

// ============================================================
// Helpers
// ============================================================

// ============================================================
// Agent Position Group (Placard Style)
// ============================================================
const AgentPositionGroup: React.FC<{
  agentEmoji: string;
  agentName: string;
  positions: AgentTrade[];
  onAgentClick: () => void;
  onTakePosition: (trade: AgentTrade) => void;
}> = ({ agentEmoji, agentName, positions, onAgentClick, onTakePosition }) => {
  const [expanded, setExpanded] = useState(false);
  const totalPnl = positions.reduce((s, t) => s + (t.position.unrealized_pnl || 0), 0);
  // Capital-weighted ROI = ΣP&L / Σcapital-at-risk (an unweighted mean of per-position ROI%
  // is statistically wrong — a $1k position can't count equally with a $50k one).
  const totalCapital = positions.reduce((s, t) => s + (t.position.buying_power_used || 0), 0);
  const totalPnlPct = totalCapital > 0 ? (totalPnl / totalCapital) * 100 : 0;
  const isProfitable = totalPnl >= 0;
  const symbols = Array.from(new Set(positions.map(t => t.position.symbol)));
  const borderColor = isProfitable ? 'border-cyan-500/20' : 'border-red-500/20';
  const bgColor = isProfitable ? 'bg-cyan-500/5' : 'bg-red-500/5';
  // Static class strings — Tailwind JIT cannot see interpolated `${accentColor}` names,
  // so the old hover:bg-${accentColor}-500/10 was silently dropped from the built CSS.
  const hoverBgClass = isProfitable ? 'hover:bg-cyan-500/10' : 'hover:bg-red-500/10';
  const hoverBorderClass = isProfitable ? 'hover:border-cyan-500/30' : 'hover:border-red-500/30';

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden`}>
      {/* Header — placard style matching DashboardDiscussionCard */}
      <div
        className={`flex items-start gap-2.5 px-3 pt-3 pb-2 cursor-pointer ${hoverBgClass} transition-colors`}
        onClick={() => setExpanded(!expanded)}
      >
        <div
          className={`shrink-0 w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center text-base border border-slate-600/20 cursor-pointer ${hoverBorderClass} transition-colors`}
          onClick={(e) => { e.stopPropagation(); onAgentClick(); }}
        >
          {agentEmoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="text-xs font-bold text-white hover:text-cyan-400 transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); onAgentClick(); }}
            >
              {agentName}
            </span>
            {symbols.map(sym => (
              <span key={sym} className="text-10 px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded font-mono font-semibold border border-cyan-500/15">
                ${sym}
              </span>
            ))}
            <span className="text-9 px-1.5 py-0.5 rounded font-bold border bg-slate-500/15 text-slate-400 border-slate-600/20">
              {positions.length} {positions.length === 1 ? 'position' : 'positions'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-10 font-bold font-mono tabular-nums flex items-center gap-0.5 ${isProfitable ? 'text-green-400' : 'text-red-400'}`}>
              {isProfitable ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
              {isProfitable ? '+' : ''}${totalPnl.toFixed(0)}
            </span>
            <span className={`text-10 font-mono tabular-nums ${isProfitable ? 'text-green-400/60' : 'text-red-400/60'}`}>
              {isProfitable ? '+' : ''}{totalPnlPct.toFixed(1)}%
            </span>
            {expanded
              ? <ChevronDown className="w-3 h-3 text-slate-500 ml-auto" />
              : <ChevronRight className="w-3 h-3 text-slate-500 ml-auto" />}
          </div>
        </div>
      </div>

      {/* Position Cards */}
      {expanded && (
        <div className="px-3 pb-2 space-y-1.5 pl-[46px]">
          {positions.map((trade, idx) => {
            const pos = trade.position;
            const ind = pos.entry_indicators || {};
            const pnl = pos.unrealized_pnl || 0;
            const pnlPct = pos.unrealized_roi_pct ?? pos.unrealized_pnl_pct ?? 0;
            const isPos = pnl >= 0;
            const isShares = pos.strategy === 'SHARES';
            const hasStockLeg = pos.has_stock_leg ?? false;
            const strat = pos.strategy === 'Cash-Secured Put' ? 'CSP' : pos.strategy === 'Covered Call' ? 'CC' : pos.strategy === 'SHARES' ? 'Shares' : pos.strategy;
            const prem = pos.entry_price * pos.contracts * 100;
            const dte = dteBadgeDays(pos.expiration);   // B-026: ET days, not the viewer's
            const dteColor = dte < 0 ? 'text-red-400 bg-red-500/10' : dte <= 7 ? 'text-yellow-400 bg-yellow-500/10' : 'text-slate-400 bg-slate-700/30';
            const rsiVal = ind.rsi ?? 0;
            const rsiColor = rsiVal <= 30 ? 'text-green-400' : rsiVal >= 70 ? 'text-red-400' : 'text-slate-300';
            const macdColor = ind.macd_signal === 'bullish' ? 'text-green-400' : ind.macd_signal === 'bearish' ? 'text-red-400' : 'text-slate-300';
            const hasIndicators = rsiVal > 0 || ind.macd_signal || ind.support_level;
            const tfSignals = ind.timeframe_signals;

            return (
              <div key={pos.id || idx} className="group">
                <div className="px-3 py-1.5 bg-slate-800/60 border border-slate-700/30 rounded-lg">
                  {/* Row 1: $SYMBOL + strategy badge + DTE */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white font-mono">${pos.symbol}</span>
                      <span className={`text-9 px-1.5 py-0.5 rounded font-medium ${
                        strat === 'CSP' ? 'bg-blue-500/10 text-blue-400' : strat === 'Shares' ? 'bg-purple-500/10 text-purple-400' : 'bg-amber-500/10 text-amber-400'
                      }`}>{strat}</span>
                    </div>
                    <span className={`text-10 font-mono px-1 py-0.5 rounded ${isShares ? 'text-slate-600 bg-slate-700/30' : dteColor}`}>
                      {isShares ? <span className="text-slate-500">No expiry</span> : dte < 0 ? 'Expired' : <><span className="text-slate-500">Exp</span> {pos.expiration?.slice(5)} <span className="text-slate-600">·</span> {dte}d left</>}
                    </span>
                  </div>

                  {/* Row 2: Contract details — single inline row */}
                  <div className="flex items-center gap-2 text-10 mb-1 pb-1 border-b border-slate-700/20 flex-wrap">
                    {isShares ? (
                      /* SHARES: show stock entry/current, share count */
                      <>
                        <span className="text-slate-500">Entry <span className="text-white font-mono">${pos.stock_entry_price?.toFixed(2) || '0.00'}</span></span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">Current <span className="text-white font-mono">${(pos.current_stock_price || pos.stock_entry_price || 0).toFixed(2)}</span></span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">{pos.contracts * 100} shares</span>
                      </>
                    ) : (
                      /* CSP / CC: option details */
                      <>
                        <span className="text-slate-500">Strike <span className="text-white font-mono">${pos.strike.toFixed(0)}</span></span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">Price <span className="text-white font-mono">${(ind.stock_price || pos.current_stock_price || pos.strike).toFixed(1)}</span></span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">Prem <span className="text-green-400 font-mono">{ind.premium_pct ? `${ind.premium_pct.toFixed(2)}%` : `$${pos.entry_price.toFixed(2)}`}</span></span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">×{pos.contracts}</span>
                        <span className="text-green-400/60 font-mono">+${prem.toFixed(0)}</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">Opt</span>
                        <span className="text-slate-400 font-mono">${pos.entry_price.toFixed(2)}</span>
                        {pos.current_price != null && pos.current_price > 0 && (
                          <>
                            <span className="text-slate-600">→</span>
                            <span className={`font-mono ${((pos.option_pnl ?? 0) >= 0) ? 'text-green-400' : 'text-red-400'}`}>${pos.current_price.toFixed(2)}</span>
                          </>
                        )}
                        {/* CC: show stock entry → current */}
                        {hasStockLeg && (
                          <>
                            <span className="text-slate-600">·</span>
                            <span className="text-slate-500">Stk</span>
                            <span className="text-slate-400 font-mono">${pos.stock_entry_price!.toFixed(1)}</span>
                            <span className="text-slate-600">→</span>
                            <span className={`font-mono ${pnlColor(pos.stock_pnl ?? 0)}`}>${pos.current_stock_price!.toFixed(1)}</span>
                          </>
                        )}
                      </>
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                      {/* CC: show split P&L (not for SHARES — pure stock) */}
                      {!isShares && hasStockLeg ? (
                        <>
                          <span className={`text-9 font-mono tabular-nums ${pnlColor50(pos.option_pnl ?? 0)}`}>
                            Opt:{(pos.option_pnl ?? 0) >= 0 ? '+' : ''}{(pos.option_pnl ?? 0).toFixed(0)}
                          </span>
                          <span className={`text-9 font-mono tabular-nums ${pnlColor50(pos.stock_pnl ?? 0)}`}>
                            Stk:{(pos.stock_pnl ?? 0) >= 0 ? '+' : ''}{(pos.stock_pnl ?? 0).toFixed(0)}
                          </span>
                          <span className="text-slate-700">|</span>
                        </>
                      ) : null}
                      <span className={`text-11 font-bold font-mono tabular-nums ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                        {isPos ? '+' : ''}${Math.abs(pnl).toFixed(0)}
                      </span>
                      <span className={`text-9 font-mono tabular-nums ${isPos ? 'text-green-400/50' : 'text-red-400/50'}`}>
                        {isPos ? '+' : ''}{pnlPct.toFixed(1)}% ROI
                      </span>
                      {pos.premium_pnl_pct != null && (
                        <span className={`text-9 font-mono tabular-nums ${pnlColor30(pos.premium_pnl_pct)}`}>
                          {pos.premium_pnl_pct >= 0 ? '+' : ''}{pos.premium_pnl_pct.toFixed(0)}%p
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onTakePosition(trade); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-400 rounded border border-cyan-500/20"
                        title="Copy this trade"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Row 3: Technicals + Confluence + MTF — single inline flow */}
                  {(hasIndicators || (ind.confluence_score != null && ind.confluence_score > 0) || tfSignals || ind.momentum_confirmed) && (
                    <div className="flex items-center gap-1.5 text-9 flex-wrap">
                      {rsiVal > 0 && (
                        <span className="text-slate-500">RSI <span className={`font-mono ${rsiColor}`}>{rsiVal.toFixed(0)}{ind.rsi_signal === 'oversold' ? '↓' : ind.rsi_signal === 'overbought' ? '↑' : ''}</span></span>
                      )}
                      {ind.macd_signal && (
                        <span className="text-slate-500">MACD <span className={`font-mono ${macdColor}`}>{ind.macd_signal}</span></span>
                      )}
                      {(ind.support_level != null && ind.support_level > 0) && (
                        <span className="text-slate-500">Sup <span className="text-white font-mono">${ind.support_level.toFixed(0)}</span></span>
                      )}
                      {(ind.resistance_level != null && ind.resistance_level > 0) && (
                        <span className="text-slate-500">Res <span className="text-white font-mono">${ind.resistance_level.toFixed(0)}</span></span>
                      )}
                      {ind.volume_signal && (
                        <span className="text-slate-500">Vol <span className="text-slate-300 font-mono">{ind.volume_signal}{ind.volume_ratio ? ` ${ind.volume_ratio.toFixed(1)}x` : ''}</span></span>
                      )}
                      {tfSignals && (['weekly', 'daily', '4h', '1h'] as const).map(tf => {
                        const sig = tfSignals[tf];
                        if (!sig || sig === 'N/A') return null;
                        const c = sig === 'bullish' ? 'bg-green-500/20 text-green-400' : sig === 'bearish' ? 'bg-red-500/20 text-red-400' : 'bg-slate-600/30 text-slate-400';
                        return <span key={tf} className={`text-9 px-1 py-0.5 rounded font-mono ${c}`}>{tf.toUpperCase()}{sig === 'bullish' ? '↑' : sig === 'bearish' ? '↓' : '—'}</span>;
                      })}
                      {ind.momentum_confirmed && <span className="text-9 px-1 py-0.5 bg-blue-500/10 text-blue-400 rounded">Mom✓</span>}
                    </div>
                  )}

                  {/* Row 4: Opened timestamp */}
                  <div className="flex items-center gap-1 text-9 mt-0.5">
                    <span className="text-slate-600">Opened</span>
                    <span className="text-slate-500 font-mono">{formatDateTime(pos.entry_date)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Compact Leaderboard Strip (Bottom)
// ============================================================
const LeaderboardStrip: React.FC<{
  agents: AgentLeaderboardEntry[];
  benchmarks: { spy: number | null; qqq: number | null };
  loading: boolean;
  onAgentClick: (id: string) => void;
}> = ({ agents, benchmarks, loading, onAgentClick }) => {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-2">
        <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
      </div>
    );
  }

  const displayAgents = agents;

  return (
    <div className="border-t border-slate-700/30 bg-slate-800/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-800/50 transition-colors"
      >
        <Trophy className="w-3.5 h-3.5 text-yellow-400" />
        <span className="text-10 font-bold text-slate-400">LEADERBOARD</span>
        <div className="flex-1 flex items-center gap-1 overflow-hidden">
          {!expanded && (
            <>
              {displayAgents.slice(0, 5).map((entry, idx) => {
                const ret = entry.performance.total_return_pct ?? entry.performance.total_pnl_pct;
                const pnlPos = ret >= 0;
                return (
                  <span key={entry.agent.id} className="shrink-0 text-10 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-700/30">
                    <span>{idx === 0 ? '\u{1F947}' : idx === 1 ? '\u{1F948}' : idx === 2 ? '\u{1F949}' : `#${idx+1}`}</span>
                    <span>{entry.agent.emoji}</span>
                    <span className={`font-mono font-bold ${pnlPos ? 'text-green-400' : 'text-red-400'}`}>
                      {pnlPos ? '+' : ''}{ret.toFixed(1)}%
                    </span>
                  </span>
                );
              })}
              {benchmarks.spy !== null && (
                <span className="shrink-0 text-10 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-700/40 border border-slate-600/20">
                  <span className="text-9 text-slate-400">SPY</span>
                  <span className={`font-mono font-bold ${(benchmarks.spy ?? 0) >= 0 ? 'text-slate-300' : 'text-red-300'}`}>
                    {(benchmarks.spy ?? 0) >= 0 ? '+' : ''}{(benchmarks.spy ?? 0).toFixed(1)}%
                  </span>
                </span>
              )}
              {benchmarks.qqq !== null && (
                <span className="shrink-0 text-10 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-700/40 border border-slate-600/20">
                  <span className="text-9 text-slate-400">QQQ</span>
                  <span className={`font-mono font-bold ${(benchmarks.qqq ?? 0) >= 0 ? 'text-slate-300' : 'text-red-300'}`}>
                    {(benchmarks.qqq ?? 0) >= 0 ? '+' : ''}{(benchmarks.qqq ?? 0).toFixed(1)}%
                  </span>
                </span>
              )}
            </>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-2 grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {displayAgents.map((entry, idx) => {
            const { agent, performance } = entry;
            const ret = performance.total_return_pct ?? performance.total_pnl_pct;
            const pnlPos = ret >= 0;
            const nav = performance.nav || agent.current_capital;
            return (
              <button
                key={agent.id}
                onClick={() => onAgentClick(agent.id)}
                className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/40 border border-slate-700/30 hover:border-cyan-500/30 transition-all text-left group"
              >
                <div className="flex items-center gap-1 min-w-[28px]">
                  <span className="text-9 text-slate-500">{idx === 0 ? '\u{1F947}' : idx === 1 ? '\u{1F948}' : idx === 2 ? '\u{1F949}' : `#${idx+1}`}</span>
                  <span className="text-sm">{agent.emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-10 font-semibold text-white truncate group-hover:text-cyan-400 transition-colors">{agent.name}</div>
                  <div className="flex items-center gap-2">
                    <span className={`text-10 font-bold font-mono tabular-nums ${pnlPos ? 'text-green-400' : 'text-red-400'}`}>
                      {pnlPos ? '+' : ''}{ret.toFixed(2)}%
                    </span>
                    <span className="text-9 text-slate-600 font-mono">${(nav / 1000).toFixed(0)}k</span>
                  </div>
                </div>
              </button>
            );
          })}
          {[{ label: 'SPY', ret: benchmarks.spy }, { label: 'QQQ', ret: benchmarks.qqq }].map(b => b.ret !== null && (
            <div
              key={b.label}
              className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/20 border border-slate-600/20"
            >
              <div className="flex items-center gap-1 min-w-[28px]">
                <span className="text-xs">📊</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-10 font-semibold text-slate-400">{b.label}</div>
                <span className={`text-10 font-bold font-mono tabular-nums ${(b.ret ?? 0) >= 0 ? 'text-slate-300' : 'text-red-300'}`}>
                  {(b.ret ?? 0) >= 0 ? '+' : ''}{(b.ret ?? 0).toFixed(2)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Main Dashboard Component
// ============================================================
interface DashboardProps {
  agentTrades: AgentTrade[];
  onTakePosition: (trade: AgentTrade) => void;
  onNavigateToChat?: () => void;
  isOrchestrating?: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({
  agentTrades,
  onTakePosition,
  onNavigateToChat,
  isOrchestrating,
}) => {
  const [agents, setAgents] = useState<AgentLeaderboardEntry[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [activeView, setActiveView] = useState<'today' | 'positions'>('today');
  const [dailySummary, setDailySummary] = useState<DailySummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [benchmarks, setBenchmarks] = useState<{ spy: number | null; qqq: number | null }>({ spy: null, qqq: null });

  const fetchAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const data = await apiService.getAgentLeaderboard(20);
      setAgents(data || []);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const data = await apiService.getDailySummary();
      setDailySummary(data);
    } catch (err) {
      console.error('Failed to fetch daily summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchBenchmarks = useCallback(async () => {
    try {
      const data = await apiService.getBenchmarks('ALL');
      const spyPoints = data?.SPY || [];
      const qqqPoints = data?.QQQ || [];
      setBenchmarks({
        spy: spyPoints.length > 0 ? spyPoints[spyPoints.length - 1].return_pct : null,
        qqq: qqqPoints.length > 0 ? qqqPoints[qqqPoints.length - 1].return_pct : null,
      });
    } catch (err) {
      console.error('Failed to fetch benchmarks:', err);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchSummary();
    fetchBenchmarks();
  }, [fetchAgents, fetchSummary, fetchBenchmarks]);

  // Refetch after orchestration completes
  const prevOrchestrating = React.useRef(false);
  useEffect(() => {
    if (prevOrchestrating.current && !isOrchestrating) {
      fetchAgents();
      fetchSummary();
    }
    prevOrchestrating.current = !!isOrchestrating;
  }, [isOrchestrating, fetchAgents, fetchSummary]);

  const groupedPositions = agentTrades.reduce<Record<string, AgentTrade[]>>((acc, trade) => {
    const key = trade.agent.id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(trade);
    return acc;
  }, {});

  const sortedGroups = Object.entries(groupedPositions).sort(([, a], [, b]) => {
    const aPnl = a.reduce((s, t) => s + (t.position.unrealized_pnl || 0), 0);
    const bPnl = b.reduce((s, t) => s + (t.position.unrealized_pnl || 0), 0);
    return bPnl - aPnl;
  });

  const openPositionCount = agentTrades.length;

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Tab Switcher */}
        <div className="flex items-center border-b border-slate-700/30 shrink-0">
          <button
            onClick={() => setActiveView('today')}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all border-b-2 ${activeView === 'today' ? 'text-cyan-400 border-cyan-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
          >
            <Sun className="w-3.5 h-3.5" />
            Today
          </button>
          <button
            onClick={() => setActiveView('positions')}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all border-b-2 ${activeView === 'positions' ? 'text-cyan-400 border-cyan-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Positions
            <span className="text-10 px-1.5 py-0.5 bg-slate-800 rounded ml-1">{openPositionCount}</span>
          </button>
          {onNavigateToChat && (
            <button
              onClick={onNavigateToChat}
              className="ml-auto mr-2 px-3 py-1 text-10 font-medium text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded-md border border-purple-500/25 transition-colors flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              All Chats
            </button>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto pr-1 min-h-0 py-3">
          {activeView === 'today' ? (
            <TodaySummary
              summary={dailySummary}
              loading={loadingSummary}
              agentTrades={agentTrades}
              onTakePosition={onTakePosition}
            />
          ) : (
            sortedGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Bot className="w-12 h-12 text-slate-600 mb-3" />
                <p className="text-sm text-slate-500">No open positions yet</p>
                <p className="text-11 text-slate-600 mt-1">Hit <span className="text-cyan-400 font-semibold">Run Cycle</span> to trigger agent trading decisions</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedGroups.map(([agentId, trades]) => (
                  <AgentPositionGroup
                    key={agentId}
                    agentEmoji={trades[0].agent.emoji}
                    agentName={trades[0].agent.name}
                    positions={trades}
                    onAgentClick={() => setSelectedAgent(agentId)}
                    onTakePosition={onTakePosition}
                  />
                ))}
              </div>
            )
          )}
        </div>

        {/* Leaderboard Strip */}
        <div className="shrink-0">
          <LeaderboardStrip agents={agents} benchmarks={benchmarks} loading={loadingAgents} onAgentClick={(id) => setSelectedAgent(id)} />
        </div>
      </div>

      {selectedAgent && <AgentDetailModal agentId={selectedAgent} onClose={() => setSelectedAgent(null)} />}
    </>
  );
};

export default Dashboard;
